import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Text } from 'grommet';
import { Checkmark, Previous, Refresh } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import {
  Seo, AppAnchor, AppButton, Spinning,
} from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import {
  fetchPayoutStatus,
  clearPayoutStatus,
  deletePayoutAccountRequest,
} from '@Actions';
import { paths } from '@Components/configs';
import { StyledStatusBadge } from './StyledAccountPage';

const PayoutStatusPage = ({
  small,
  location,
  paymentPending,
  payoutStatus,
  payoutStatusLoading,
  fetchStatus,
  clearStatus,
  disconnectPayout,
}) => {
  const {
    theme: { page, button },
    isDarkMode,
  } = useAppTheme();
  const navigate = useNavigate();
  const [confirmDisconnect, setConfirmDisconnect] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);

  React.useEffect(() => {
    if (!paymentPending) fetchStatus();
    return () => clearStatus();
  }, [paymentPending]); // eslint-disable-line

  const usePageBg = isDarkMode ? page.background : page.altBg;
  const useSectionBg = isDarkMode ? page.altBg : page.background;

  const setupComplete = payoutStatus?.setupComplete === true;

  const renderContent = () => {
    if (paymentPending) {
      return (
        <Box direction="column" gap="1rem">
          <Text size="1rem" color={page.primaryText}>
            Payout setup is not available until your payment is complete.
          </Text>
          <AppAnchor path={paths.account}>
            <AppButton
              overrideHover
              extraWide
              level="authSubmit"
              bgColor={button.primaryBg}
              color="white"
              label="Back to account"
              alignSelf="start"
            />
          </AppAnchor>
        </Box>
      );
    }

    if (payoutStatusLoading && !payoutStatus) {
      return (
        <Box
          direction="column"
          align="center"
          justify="center"
          gap="1.25rem"
          pad="1rem"
        >
          <Spinning size="large" color={page.highlight} />
        </Box>
      );
    }

    if (setupComplete) {
      const handleDisconnect = async () => {
        setDisconnecting(true);
        await disconnectPayout(() => navigate(paths.account));
        setDisconnecting(false);
        setConfirmDisconnect(false);
      };

      return (
        <Box direction="column" gap="1.5rem">
          <Box direction="row" align="center" gap="0.75rem">
            <StyledStatusBadge background="#EDFDF5">
              <Box direction="row" align="center" gap="0.4rem">
                <Checkmark size="small" color="var(--app-success-dark)" />
                <Text
                  size="1.1rem"
                  color="var(--app-success-dark)"
                  weight={600}
                >
                  Payouts enabled
                </Text>
              </Box>
            </StyledStatusBadge>
          </Box>
          <Text size="1rem" color={page.primaryText}>
            Your bank account is connected and you are set up to receive
            earnings.
          </Text>
          {!confirmDisconnect ? (
            <Box>
              <AppButton
                overrideHover
                level="authSubmit"
                bgColor="transparent"
                color="var(--app-error)"
                label="Disconnect account"
                alignSelf="start"
                style={{ border: '1px solid var(--app-error)' }}
                onClick={() => setConfirmDisconnect(true)}
              />
            </Box>
          ) : (
            <Box direction="column" gap="0.75rem">
              <Text size="0.9rem" color={page.primaryText}>
                Are you sure? This will permanently remove your connected bank
                account.
              </Text>
              <Box direction="row" align="center" gap="0.5rem">
                <AppButton
                  overrideHover
                  level="tableControls"
                  bgColor="var(--app-error)"
                  color="white"
                  label={disconnecting ? 'Disconnecting…' : 'Confirm'}
                  disabled={disconnecting}
                  onClick={handleDisconnect}
                />
                <Text
                  size="0.9rem"
                  color={page.primaryText}
                  style={{
                    textDecoration: 'underline',
                    cursor: disconnecting ? 'not-allowed' : 'pointer',
                    opacity: disconnecting ? 0.5 : 1,
                  }}
                  onClick={
                    disconnecting
                      ? undefined
                      : () => setConfirmDisconnect(false)
                  }
                >
                  Cancel
                </Text>
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Box direction="column" gap="1.5rem">
        <Box direction="row" align="center" gap="0.75rem">
          <StyledStatusBadge background="#FCF8EF">
            <Text size="1.1rem" color="#AB5741" weight={600}>
              Verification pending
            </Text>
          </StyledStatusBadge>
        </Box>
        <Text size="1rem" color={page.primaryText}>
          Your account is still being verified, please check back soon. This
          typically takes 1-2 business days.
        </Text>
        {setupComplete === false && (
          <Text size="1rem" color={page.primaryText}>
            If you haven&apos;t completed the payout setup process, please click the
            button below to finish setting up your account with Stripe.
          </Text>
        )}
        <Box>
          <AppAnchor path={paths.payoutSetup}>
            <AppButton
              overrideHover
              extraWide
              level="authSubmit"
              bgColor={button.primaryBg}
              color="white"
              label="Continue setup in Stripe"
              alignSelf="start"
            />
          </AppAnchor>
        </Box>
        <AppAnchor path={paths.account}>
          <Text
            size="0.9rem"
            color={page.altHighlight}
            style={{ textDecoration: 'underline' }}
          >
            Back to account
          </Text>
        </AppAnchor>
      </Box>
    );
  };

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Payout status',
        subTitle: 'Plan and profile settings',
        bannerChildrenPosition: 'bottom',
        bannerChildren: (
          <Box width="fit-content" direction="row" align="center" gap="1rem">
            <AppButton
              overrideHover
              onClick={() => navigate(paths.account)}
              label="Go back"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={(
                <Previous
                  size="0.8rem"
                  color={isDarkMode ? button.primaryText : page.primaryText}
                />
              )}
            />
            <AppButton
              overrideHover
              onClick={() => fetchStatus()}
              label="Refresh"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Refresh size="0.9rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
              disabled={payoutStatusLoading}
            />
          </Box>
        ),
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '2rem', horizontal: small ? '1rem' : '1.5rem' }}
      >
        <Box
          direction="column"
          gap="1.5rem"
          background={useSectionBg}
          round="var(--app-radius)"
          pad="1.5rem"
          style={{ maxWidth: '640px' }}
        >
          <Box
            width={small ? '55%' : '25%'}
            border={{
              color: 'var(--nav-border)',
              size: '1px',
              style: 'solid',
              side: 'bottom',
            }}
            style={{ minWidth: '15rem' }}
          >
            <Box
              flex={false}
              border={{
                color: 'var(--page-url-color)',
                size: '1px',
                style: 'solid',
                side: 'bottom',
              }}
              justify="end"
              pad={{ bottom: '0.5rem' }}
              width="fit-content"
            >
              <Text color={page.primaryText} weight={600} size="1.25rem">
                Payout status details
              </Text>
            </Box>
          </Box>
          {renderContent()}
        </Box>
      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    paymentPending: state.auth.paymentPending,
    payoutStatus: state.payment.payoutStatus,
    payoutStatusLoading: state.payment.payoutStatusLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchStatus: fetchPayoutStatus,
      clearStatus: clearPayoutStatus,
      disconnectPayout: deletePayoutAccountRequest,
    },
    dispatch,
  );
}

PayoutStatusPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  paymentPending: PropTypes.bool.isRequired,
  payoutStatus: PropTypes.shape({
    stripeAccountId: PropTypes.string,
    setupComplete: PropTypes.bool,
    capabilities: PropTypes.objectOf(PropTypes.string),
  }),
  payoutStatusLoading: PropTypes.bool.isRequired,
  fetchStatus: PropTypes.func.isRequired,
  clearStatus: PropTypes.func.isRequired,
  disconnectPayout: PropTypes.func.isRequired,
};

PayoutStatusPage.defaultProps = {
  payoutStatus: null,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withProductAuth(PayoutStatusPage));
